import { useState } from "react";

/**
 * Delivery-aware primary CTA on the tool detail page.
 *
 * Renders different action surfaces depending on `tool.delivery[0]`:
 *
 *   - self_host  → copy-to-clipboard install snippet (pip install / git clone / docker run)
 *                  Default to `git clone <repo>.git` if URL is a github repo and no
 *                  install_command set.
 *   - cli        → same copy-snippet surface as self_host but labeled "install cli"
 *                  (npm i -g foo / pipx install foo / brew install foo).
 *   - api        → copy-snippet for a curl one-liner so a developer can try it instantly.
 *   - mcp        → "Open in..." menu (Cursor / Claude Code / VS Code / Codex) — direct
 *                  deep links wrapping the MCP server URL. install_command holds the
 *                  bare server endpoint (https://server.example.com/mcp).
 *   - claude_skill → "Install in Claude →" bold button.
 *   - chatgpt_gpt → "Open in ChatGPT →" bold button.
 *   - extension  → "Add to browser →" bold button.
 *   - everything else (web, mobile, desktop) → renders nothing; the
 *     existing `[visit →]` link on ToolDetail remains the primary CTA.
 *
 * Brutalist styling: black-bordered single-line monospace pre, [copy] inline link.
 */
function CopySnippet({ label, snippet, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail on http or sandboxed iframes — silently
      // skip; user can select-and-copy the visible text manually.
    }
  };
  return (
    <div className="mt-3 mb-1" data-testid={testid}>
      <div className="text-[11px] text-[#888] mb-1">{label}</div>
      <div className="flex items-stretch border border-black bg-[#fafafa]">
        <pre className="flex-1 m-0 px-3 py-2 text-[12px] font-mono whitespace-pre overflow-x-auto">
          $ {snippet}
        </pre>
        <button
          type="button"
          onClick={onCopy}
          className="border-l border-black px-3 text-[12px] font-mono hover:bg-black hover:text-white whitespace-nowrap"
          data-testid="tool-cta-copy"
        >
          {copied ? "[ copied ]" : "[ copy ]"}
        </button>
      </div>
    </div>
  );
}

function CTAButton({ label, href, hint, testid }) {
  if (!href) return null;
  return (
    <div className="mt-3 mb-1" data-testid={testid}>
      <a
        href={href}
        target="_blank"
        rel="noopener nofollow noreferrer"
        className="tv-darklink inline-flex items-center border border-black bg-black px-3 py-2 text-[13px] font-mono transition-colors"
      >
        {label}
      </a>
      {hint && <span className="ml-3 text-[11px] text-[#888]">{hint}</span>}
    </div>
  );
}

/**
 * MCP "Open in..." multi-link button row. Wraps the MCP server URL into
 * each client's documented one-click install / deep-link format. Falls
 * back to a plain copy snippet when we can't compose a deep link (e.g.
 * the URL doesn't look like an http endpoint).
 */
function OpenInMcp({ serverUrl }) {
  if (!serverUrl) return null;
  const enc = encodeURIComponent(serverUrl);
  const targets = [
    // Cursor uses a custom URI scheme for one-click MCP install.
    { label: "Cursor", href: `cursor://anysphere.cursor-deeplink/mcp/install?url=${enc}` },
    { label: "Claude Code", href: `https://claude.ai/mcp?url=${enc}` },
    { label: "VS Code", href: `vscode://mcp/install?url=${enc}` },
  ];
  return (
    <div className="mt-3 mb-1" data-testid="tool-cta-mcp">
      <div className="text-[11px] text-[#888] mb-1">open in...</div>
      <div className="flex flex-wrap gap-2">
        {targets.map((t) => (
          <a
            key={t.label}
            href={t.href}
            target="_blank"
            rel="noopener nofollow noreferrer"
            className="border border-black px-3 py-2 text-[12px] font-mono hover:bg-black hover:text-white transition-colors"
            data-testid={`tool-cta-mcp-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            [ {t.label} ]
          </a>
        ))}
        <span className="self-center text-[11px] text-[#888] ml-1">
          server: <code className="font-mono">{serverUrl}</code>
        </span>
      </div>
    </div>
  );
}

export default function ToolCTA({ tool }) {
  const delivery = Array.isArray(tool.delivery) ? tool.delivery : [];
  const primary = delivery[0];

  // --- open source: install snippet (pip install / git clone / docker run) ---
  // Driven by the is_open_source property now (not a delivery channel).
  // Skip when a more specific actionable delivery CTA applies (mcp/api/cli).
  if (tool.is_open_source && !["mcp", "api", "cli"].includes(primary)) {
    let snippet = tool.install_command;
    if (!snippet) {
      const u = tool.url || "";
      if (/github\.com\/[^/]+\/[^/]+/i.test(u)) {
        snippet = `git clone ${u.replace(/\/$/, "")}.git`;
      }
    }
    if (!snippet) return null;
    return <CopySnippet label="install" snippet={snippet} testid="tool-cta-open-source" />;
  }

  // --- cli: install snippet (distinct label) ---
  if (primary === "cli") {
    if (!tool.install_command) return null;
    return <CopySnippet label="install cli" snippet={tool.install_command} testid="tool-cta-cli" />;
  }

  // --- api: curl one-liner ---
  if (primary === "api") {
    if (!tool.install_command) return null;
    return <CopySnippet label="try" snippet={tool.install_command} testid="tool-cta-api" />;
  }

  // --- mcp: open-in dropdown ---
  if (primary === "mcp") {
    return <OpenInMcp serverUrl={tool.install_command || tool.url} />;
  }

  // --- claude_skill: button label switches based on link type.
  //     Checkout pages (Lemon Squeezy, Gumroad, Stripe etc.) → "buy"
  //     Direct artifact / claude.ai import URL → "install in claude"
  //     Bare vendor page → "open" (least specific fallback). ---
  if (primary === "claude_skill") {
    const href = tool.install_command || tool.url;
    if (!href) return null;
    const lower = href.toLowerCase();
    const isCheckout =
      /lemonsqueezy\.com|gumroad\.com|stripe\.com\/c\/|checkout|\/pay\/|\/buy\//.test(lower);
    const isClaudeImport =
      /claude\.ai\/skill|claude\.ai\/import|\.skill\.json|\.skill$|\/import\?/.test(lower);
    let label, hint;
    if (isClaudeImport) {
      label = "[ install in claude → ]";
      hint = "imports into your Claude Pro+ account";
    } else if (isCheckout) {
      label = "[ buy claude skill → ]";
      // Surface the checkout vendor name so users know where they're going.
      let store = "checkout";
      if (lower.includes("lemonsqueezy")) store = "Lemon Squeezy";
      else if (lower.includes("gumroad")) store = "Gumroad";
      else if (lower.includes("stripe")) store = "Stripe Checkout";
      hint = `${store} — skill is delivered after purchase`;
    } else {
      label = "[ open claude skill → ]";
      hint = "requires Claude Pro+";
    }
    return (
      <CTAButton
        label={label}
        href={href}
        hint={hint}
        testid="tool-cta-claude-skill"
      />
    );
  }

  // --- prompt: raw prompt artifact (paste-anywhere text). Label
  //     switches the same way claude_skill does — checkout vs. direct
  //     download artifact. Distinct from claude_skill because the buyer
  //     pastes the text into ANY LLM, not just Claude. ---
  if (primary === "prompt") {
    const href = tool.install_command || tool.url;
    if (!href) return null;
    const lower = href.toLowerCase();
    const isCheckout =
      /lemonsqueezy\.com|gumroad\.com|promptbase\.com|stripe\.com\/c\/|checkout|\/pay\/|\/buy\//.test(lower);
    const isDirectArtifact = /\.txt$|\.md$|\.json$|\/download/.test(lower);
    let label, hint;
    if (isDirectArtifact) {
      label = "[ download prompt → ]";
      hint = "raw prompt — paste into any LLM (Claude, ChatGPT, Gemini)";
    } else if (isCheckout) {
      label = "[ buy prompt → ]";
      let store = "checkout";
      if (lower.includes("lemonsqueezy")) store = "Lemon Squeezy";
      else if (lower.includes("gumroad")) store = "Gumroad";
      else if (lower.includes("promptbase")) store = "PromptBase";
      else if (lower.includes("stripe")) store = "Stripe Checkout";
      hint = `${store} — prompt delivered as a file after purchase`;
    } else {
      label = "[ get prompt → ]";
      hint = "paste into any LLM (Claude, ChatGPT, Gemini)";
    }
    return (
      <CTAButton
        label={label}
        href={href}
        hint={hint}
        testid="tool-cta-prompt"
      />
    );
  }

  // --- chatgpt_gpt: label switches based on whether the URL is a real
  //     GPT or just a vendor promo page. ---
  if (primary === "chatgpt_gpt") {
    const href = tool.install_command || tool.url;
    if (!href) return null;
    const isRealGpt = /chat\.openai\.com\/g\/|chatgpt\.com\/g\//.test(href.toLowerCase());
    return (
      <CTAButton
        label={isRealGpt ? "[ open in chatgpt → ]" : "[ get gpt → ]"}
        href={href}
        hint={isRealGpt ? "requires ChatGPT Plus" : "vendor page — requires ChatGPT Plus to use"}
        testid="tool-cta-chatgpt"
      />
    );
  }

  // --- extension: add-to-browser button ---
  if (primary === "extension") {
    const href = tool.install_command || tool.url;
    // Hint label tracks the store the link points at.
    let hint = "browser extension";
    if (/chrome\.google\.com\/webstore|chromewebstore\.google\.com/i.test(href || "")) hint = "Chrome Web Store";
    else if (/addons\.mozilla\.org/i.test(href || "")) hint = "Firefox Add-ons";
    else if (/apps\.apple\.com.*safari/i.test(href || "")) hint = "Safari Extensions";
    return (
      <CTAButton
        label="[ add to browser → ]"
        href={href}
        hint={hint}
        testid="tool-cta-extension"
      />
    );
  }

  return null;
}
